/**
 * Unified help-desk messaging (core.help_conversations + core.help_messages).
 *
 * Parallel to the buyer<->vendor chat in shopMessages.ts but lives in
 * core/ because the help desk spans the whole platform (vendor<->admin
 * and buyer<->admin) rather than a single shop relationship.
 *
 * Every action goes through a `core.rpc_help_*` SDF; this file is the
 * single typed entry-point the frontends call. RLS is enforced inside
 * the SDFs (search_path = '' + fully-qualified identifiers per
 * ai-rules/security-definer-search-path.md), so the wire calls here
 * never need to thread an org id explicitly -- the SDF derives it
 * from auth.uid().
 *
 * Schema routing: the shared supabase() client is bound to the `public`
 * schema (see packages/db/src/client.ts: `db: { schema: 'public' }`), so
 * every PostgREST request defaults to `/rest/v1/rpc/<name>` against
 * `public.<name>`. Because every help SDF lives in `core`, every RPC
 * call here MUST go through `.schema('core').rpc(...)` -- omitting it
 * surfaces in the browser as a 404 from PostgREST with no helpful
 * payload. Same pattern used by adminWorkQueue / siteConfig /
 * auditTrail / requestContext.
 */

import { supabase } from '../client';
import { clampPageSize, type KeysetCursor } from '../pagination';

function helpRpc() {
  return (supabase() as any).schema('core');
}

// =========================================================================
// Wire types -- mirror SDF return shapes one-for-one. Numbers are
// represented as `number` because PostgREST already serializes int as
// JSON number; we keep id/timestamp columns as strings.
// =========================================================================

export type HelpPartyKind = 'vendor' | 'buyer' | 'admin';
export type HelpConversationStatus =
  | 'open'
  | 'in_progress'
  | 'resolved'
  | 'closed';

export interface HelpConversationRow {
  id: string;
  requester_kind: HelpPartyKind;
  requester_user_id: string;
  requester_org_id: string;
  subject: string;
  status: HelpConversationStatus;
  assigned_admin_id: string | null;
  assigned_at: string | null;
  resolved_at: string | null;
  resolved_by_admin_id: string | null;
  closed_at: string | null;
  closed_by_admin_id: string | null;
  last_message_at: string;
  last_message_party: HelpPartyKind | null;
  created_at: string;
  requester_last_read_at: string | null;
  assigned_admin_last_read_at: string | null;
  // Joined display labels (filled by rpc_help_get_conversation only).
  requester_display_name?: string | null;
  requester_org_name?: string | null;
  assigned_admin_display_name?: string | null;
  // Filled by rpc_help_list_* SDFs.
  unread_for_caller?: boolean;
}

export interface HelpMessageRow {
  id: string;
  conversation_id: string;
  author_user_id: string;
  author_kind: HelpPartyKind;
  body: string;
  created_at: string;
}

export interface HelpAdminCounts {
  open_total: number;
  unassigned: number;
  assigned_to_me: number;
}

export interface HelpKeysetInput {
  cursor?: KeysetCursor | null;
  pageSize?: number;
}

export interface HelpListResult<T> {
  rows: T[];
  nextCursor: KeysetCursor | null;
}

export type HelpAdminFilter = 'mine' | 'unassigned' | 'all_open' | 'all';

// =========================================================================
// SDF wrappers
// =========================================================================

function cursorToJson(cursor: KeysetCursor | null | undefined): unknown {
  if (!cursor) return null;
  return { sortValue: cursor.sortValue, id: cursor.id };
}

function nextCursorFromRows(
  rows: Array<{ id?: string | null; last_message_at?: string | null }>,
  pageSize: number,
): KeysetCursor | null {
  if (rows.length < pageSize) return null;
  const last = rows[rows.length - 1];
  if (!last || !last.id || !last.last_message_at) return null;
  return { sortValue: last.last_message_at, id: last.id };
}

export interface OpenHelpConversationInput {
  subject: string;
  firstMessageBody: string;
}

export async function openHelpConversation(
  input: OpenHelpConversationInput,
): Promise<{ id: string }> {
  const { data, error } = await helpRpc().rpc(
    'rpc_help_open_conversation',
    {
      p_subject: input.subject,
      p_first_message_body: input.firstMessageBody,
    },
  );
  if (error) throw error;
  const id = Array.isArray(data) ? data[0]?.id : data?.id;
  if (typeof id !== 'string') {
    throw new Error('rpc_help_open_conversation returned no id');
  }
  return { id };
}

export async function sendHelpMessage(
  conversationId: string,
  body: string,
): Promise<{ id: string }> {
  const { data, error } = await helpRpc().rpc(
    'rpc_help_send_message',
    {
      p_conversation_id: conversationId,
      p_body: body,
    },
  );
  if (error) throw error;
  const id = typeof data === 'string' ? data : data?.id ?? null;
  if (typeof id !== 'string') {
    throw new Error('rpc_help_send_message returned no id');
  }
  return { id };
}

export async function listHelpConversationsForRequester(
  input: HelpKeysetInput = {},
): Promise<HelpListResult<HelpConversationRow>> {
  const pageSize = clampPageSize(input.pageSize ?? 25);
  const { data, error } = await helpRpc().rpc(
    'rpc_help_list_conversations_for_requester',
    {
      p_cursor: cursorToJson(input.cursor),
      p_limit: pageSize,
    },
  );
  if (error) throw error;
  const rows = (data ?? []) as HelpConversationRow[];
  return { rows, nextCursor: nextCursorFromRows(rows, pageSize) };
}

export interface ListHelpConversationsForAdminInput extends HelpKeysetInput {
  filter: HelpAdminFilter;
}

export async function listHelpConversationsForAdmin(
  input: ListHelpConversationsForAdminInput,
): Promise<HelpListResult<HelpConversationRow>> {
  const pageSize = clampPageSize(input.pageSize ?? 25);
  const { data, error } = await helpRpc().rpc(
    'rpc_help_list_conversations_for_admin',
    {
      p_filter: input.filter,
      p_cursor: cursorToJson(input.cursor),
      p_limit: pageSize,
    },
  );
  if (error) throw error;
  const rows = (data ?? []) as HelpConversationRow[];
  return { rows, nextCursor: nextCursorFromRows(rows, pageSize) };
}

export async function getHelpConversation(
  conversationId: string,
): Promise<HelpConversationRow | null> {
  const { data, error } = await helpRpc().rpc(
    'rpc_help_get_conversation',
    { p_id: conversationId },
  );
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as HelpConversationRow | null) ?? null;
}

export interface ListHelpMessagesInput {
  conversationId: string;
  /** Keyset cursor on (created_at asc, id asc) -- oldest-first. */
  cursor?: { sortValue: string; id: string } | null;
  pageSize?: number;
}

export async function listHelpMessages(
  input: ListHelpMessagesInput,
): Promise<{ rows: HelpMessageRow[]; nextCursor: { sortValue: string; id: string } | null }> {
  const pageSize = clampPageSize(input.pageSize ?? 50);
  const { data, error } = await helpRpc().rpc('rpc_help_list_messages', {
    p_conversation_id: input.conversationId,
    p_cursor: input.cursor ? { sortValue: input.cursor.sortValue, id: input.cursor.id } : null,
    p_limit: pageSize,
  });
  if (error) throw error;
  const rows = (data ?? []) as HelpMessageRow[];
  let nextCursor: { sortValue: string; id: string } | null = null;
  if (rows.length === pageSize) {
    const last = rows[rows.length - 1];
    if (last) nextCursor = { sortValue: last.created_at, id: last.id };
  }
  return { rows, nextCursor };
}

export async function markHelpRead(conversationId: string): Promise<void> {
  const { error } = await helpRpc().rpc('rpc_help_mark_read', {
    p_conversation_id: conversationId,
  });
  if (error) throw error;
}

export async function assignHelpConversation(
  conversationId: string,
  adminId: string,
): Promise<void> {
  const { error } = await helpRpc().rpc('rpc_help_assign', {
    p_conversation_id: conversationId,
    p_admin_id: adminId,
  });
  if (error) throw error;
}

export async function resolveHelpConversation(conversationId: string): Promise<void> {
  const { error } = await helpRpc().rpc('rpc_help_resolve', {
    p_conversation_id: conversationId,
  });
  if (error) throw error;
}

export async function closeHelpConversation(conversationId: string): Promise<void> {
  const { error } = await helpRpc().rpc('rpc_help_close', {
    p_conversation_id: conversationId,
  });
  if (error) throw error;
}

export async function helpUnreadCountForCaller(): Promise<number> {
  const { data, error } = await helpRpc().rpc(
    'rpc_help_unread_count_for_caller',
  );
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

export async function helpAdminCounts(): Promise<HelpAdminCounts> {
  const { data, error } = await helpRpc().rpc('rpc_help_admin_counts');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    open_total: Number(row?.open_total ?? 0),
    unassigned: Number(row?.unassigned ?? 0),
    assigned_to_me: Number(row?.assigned_to_me ?? 0),
  };
}
